import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import ScrollToTop from './components/ScrollToTop';
import AppInitializer from './components/AppInitializer';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';

// Eagerly loaded (landing page)
import HomePage from './pages/storefront/HomePage';

// Lazy-loaded storefront pages
const ProductDetailsPage = lazy(() => import('./pages/storefront/ProductDetailsPage'));
const CategoryProductsPage = lazy(() => import('./pages/storefront/CategoryProductsPage'));
const FeaturedProductsPage = lazy(() => import('./pages/storefront/FeaturedProductsPage'));
const BestSellersPage = lazy(() => import('./pages/storefront/BestSellersPage'));
const OnSalePage = lazy(() => import('./pages/storefront/OnSalePage'));
const LoginPage = lazy(() => import('./pages/storefront/LoginPage'));
const RegisterPage = lazy(() => import('./pages/storefront/RegisterPage'));
const CartPage = lazy(() => import('./pages/storefront/CartPage'));
const CheckoutPage = lazy(() => import('./pages/storefront/CheckoutPage'));
const OrdersPage = lazy(() => import('./pages/storefront/OrdersPage'));
const OrderConfirmationPage = lazy(() => import('./pages/storefront/OrderConfirmationPage'));
const OrderDetailsPage = lazy(() => import('./pages/storefront/OrderDetailsPage'));
const NotificationsPage = lazy(() => import('./pages/storefront/NotificationsPage'));
const AboutPage = lazy(() => import('./pages/storefront/AboutPage'));
const ContactPage = lazy(() => import('./pages/storefront/ContactPage'));
const FAQPage = lazy(() => import('./pages/storefront/FAQPage'));
const ReturnsPage = lazy(() => import('./pages/storefront/ReturnsPage'));
const SearchResultsPage = lazy(() => import('./pages/storefront/SearchResultsPage'));
const ForgotPasswordPage = lazy(() => import('./pages/storefront/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/storefront/ResetPasswordPage'));

// Lazy-loaded admin pages
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Categories = lazy(() => import('./pages/admin/Categories'));
const Subcategories = lazy(() => import('./pages/admin/Subcategories'));
const Products = lazy(() => import('./pages/admin/Products'));
const Users = lazy(() => import('./pages/admin/Users'));
const AdminOrders = lazy(() => import('./pages/admin/Orders'));
const AdminNotifications = lazy(() => import('./pages/admin/Notifications'));
const AdminContentPages = lazy(() => import('./pages/admin/ContentPages'));
const AdminFAQs = lazy(() => import('./pages/admin/FAQs'));
const AdminContact = lazy(() => import('./pages/admin/Contact'));
const AdminShippingTiers = lazy(() => import('./pages/admin/ShippingTiers'));
const AdminBanners = lazy(() => import('./pages/admin/Banners'));
const AdminNewsletter = lazy(() => import('./pages/admin/Newsletter'));
const AdminSiteSettings = lazy(() => import('./pages/admin/SiteSettings'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
    </div>
  );
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-neutral-900 mb-4">{title}</h1>
        <p className="text-neutral-600 mb-8">This page is under construction</p>
        <a
          href="/"
          className="inline-block px-6 py-3 bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors"
        >
          Back to Home
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppInitializer>
        <NotificationProvider>
          <ScrollToTop />
          <Toaster position="bottom-right" richColors />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Storefront Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<PlaceholderPage title="All Products" />} />
              <Route path="/products/best-sellers" element={<BestSellersPage />} />
              <Route path="/products/featured" element={<FeaturedProductsPage />} />
              <Route path="/products/on-sale" element={<OnSalePage />} />
              <Route path="/category/:categoryId" element={<CategoryProductsPage />} />
              <Route path="/category/:categoryId/:subcategoryId" element={<CategoryProductsPage />} />
              <Route path="/product/:productId" element={<ProductDetailsPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:orderId" element={<OrderDetailsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/account" element={<PlaceholderPage title="My Account" />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/returns" element={<ReturnsPage />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/privacy" element={<PlaceholderPage title="Privacy Policy" />} />
              <Route path="/terms" element={<PlaceholderPage title="Terms of Service" />} />

              {/* Admin Routes */}
              <Route path="/admin" element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="categories" element={<Categories />} />
                <Route path="subcategories" element={<Subcategories />} />
                <Route path="products" element={<Products />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="content-pages" element={<AdminContentPages />} />
                <Route path="faqs" element={<AdminFAQs />} />
                <Route path="contact" element={<AdminContact />} />
                <Route path="users" element={<Users />} />
                <Route path="shipping" element={<AdminShippingTiers />} />
                <Route path="banners" element={<AdminBanners />} />
                <Route path="newsletter" element={<AdminNewsletter />} />
                <Route path="site-settings" element={<AdminSiteSettings />} />
              </Route>

              {/* Catch-all: redirect unknown routes to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </NotificationProvider>
      </AppInitializer>
    </BrowserRouter>
  );
}

export default App;
